import { includes } from "lodash-es";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";

import { apiClient } from "@/core/api/ApiClient";
import {
  DataUpdatesHubClientMethodName,
  EntityType,
  OnboardingDto,
  OnboardingStatus,
  OnboardingStepDefinitionDto,
  OnboardingStepType,
} from "@/core/api/generated/v1";

import { OnboardingPopperMode } from "../components/Entity/Onboarding/Popper/OnboardingPopper";
import { ROUTE_PATH } from "../constants/routing";
import {
  OnboardingHelper,
  OnboardingProgress,
  OnboardingStepPair,
} from "../helpers/entity/onboarding";
import { useCurrentTenant } from "../hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "../hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "../hooks/routing/useAppHistory";
import { useUserProfileV2 } from "../hooks/user/useUserProfileV2";
import { DataUpdatesChannelName } from "../realtime/dataUpdatesChannelName";

export interface OnboardingContextValue {
  isEnabled: boolean;
  isIniting: boolean;
  isLoading: boolean;
  onboarding: Nil<OnboardingDto>;
  enabledStepDefinitions: OnboardingStepDefinitionDto[] | null;
  currentStepPair: OnboardingStepPair | null;
  nextStepPair: OnboardingStepPair | null;
  progress: OnboardingProgress | null;
  popper: {
    isOpen: boolean;
    mode: OnboardingPopperMode;
    open: () => void;
    close: () => void;
    toggle: () => void;
    setMode: (mode: OnboardingPopperMode) => void;
  };
  fetch: () => Promise<void>;
  fetchById: (onboardingId: string) => Promise<void>;
  set: (onboarding: OnboardingDto | null) => void;
  toggleIsShowOnLaunch: () => Promise<void>;
  start: () => Promise<void>;
  complete: () => Promise<void>;
  skip: () => Promise<void>;
  // restore: () => Promise<void>;
  getStepPairByDefinitionId: (stepDefinitionId: Nil<string>) => OnboardingStepPair | null;
  getStepPairByType: (type: Nil<OnboardingStepType>) => OnboardingStepPair | null;
  /** Returns null if there is no next step. */
  getNextStepPair: (params: { currentStepDefinitionId: Nil<string> }) => OnboardingStepPair | null;
  startStep: (stepDefinitionId: string) => Promise<void>;
  completeStep: (stepDefinitionId: string) => Promise<void>;
  skipStep: (stepDefinitionId: string) => Promise<void>;
  /** Redirects to onboarding page if not started (i.e. not seen by user). Returns `true` if redirected. */
  forceRedirectOnLaunchOnce: () => boolean;
  /** Redirects to onboarding page if configured by user. Returns `true` if redirected. */
  redirectOnLaunchOnce: () => boolean;
  handleOnboardingViewed: () => Promise<void>;
}

export const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function useOnboardingContext(): OnboardingContextValue {
  const onboardingContext = useContext(OnboardingContext);
  if (!onboardingContext) {
    throw new Error("useOnboardingContext() has to be used within <OnboardingContext.Provider>");
  }
  return onboardingContext;
}

export function useOnboardingContextOptional(): OnboardingContextValue | null {
  const onboardingContext = useContext(OnboardingContext);
  return onboardingContext;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const history = useAppHistory();
  const location = useLocation();
  const currentTenant = useCurrentTenant();
  const profile = useUserProfileV2();

  const [onboarding, setOnboarding] = useState<OnboardingDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [popperMode, setPopperMode] = useState<OnboardingPopperMode>(
    OnboardingPopperMode.Collapsed,
  );

  // use ref in profile for quick checks on startup only as it's eventually updated
  const onboardingRef = profile.profile?.onboarding;
  const onboardingIdComputed = onboardingRef?.id;
  const statusComputed = onboarding?.status ?? onboardingRef?.status;
  const isShowOnLaunchComputed = onboarding?.isShowOnLaunch ?? onboardingRef?.isShowOnLaunch;

  const isEnabled = useMemo(
    () =>
      OnboardingHelper.isEnabled({
        pathname: location.pathname,
      }),
    [location.pathname],
  );

  const isIniting = profile.isIniting || (!!onboardingIdComputed && !onboarding);

  const enabledStepDefinitions = useMemo(() => {
    return onboarding?.definition?.steps?.filter((x) => x.isEnabled) || null;
  }, [onboarding]);

  const stepPairs = useMemo(() => OnboardingHelper.getStepPairs(onboarding), [onboarding]);
  const progress = useMemo(() => OnboardingHelper.getProgress(onboarding), [onboarding]);

  const stepPairsByStepDefinitionIdMap = useMemo(() => {
    return (stepPairs || []).reduce(
      (acc, step) => {
        acc[step.definition.id || ""] = step;
        return acc;
      },
      {} as Record<string, OnboardingStepPair | undefined>,
    );
  }, [stepPairs]);

  const stepPairsByTypeMap = useMemo(() => {
    return (stepPairs || []).reduce(
      (acc, step) => {
        acc[step.definition.type || OnboardingStepType.None] = step;
        return acc;
      },
      {} as Record<OnboardingStepType, OnboardingStepPair | undefined>,
    );
  }, [stepPairs]);

  const currentStepPair = useMemo(
    () => OnboardingHelper.getCurrentStepPair({ onboarding }),
    [onboarding],
  );
  const nextStepPair = useMemo(
    () =>
      OnboardingHelper.getNextStepPair({
        onboarding,
        currentStepDefinitionId: currentStepPair?.definition.id || null,
      }),
    [onboarding, currentStepPair],
  );

  const openPopper = useCallback(() => {
    setIsPopperOpen(true);
  }, []);

  const closePopper = useCallback(() => {
    setIsPopperOpen(false);
  }, []);

  const togglePopper = useCallback(() => {
    setIsPopperOpen((prev) => !prev);
  }, []);

  const getStepPairByDefinitionId = useCallback(
    (stepDefinitionId: Nil<string>) =>
      stepPairsByStepDefinitionIdMap[stepDefinitionId || ""] || null,
    [stepPairsByStepDefinitionIdMap],
  );

  const getStepPairByType = useCallback(
    (type: Nil<OnboardingStepType>) => stepPairsByTypeMap[type || OnboardingStepType.None] || null,
    [stepPairsByTypeMap],
  );

  const getNextStepPair = useCallback(
    (params: { currentStepDefinitionId: Nil<string> }) =>
      OnboardingHelper.getNextStepPair({
        onboarding,
        currentStepDefinitionId: params.currentStepDefinitionId,
      }),
    [onboarding],
  );

  const fetchById = useCallback(async (onboardingId: string) => {
    setIsLoading(true);
    try {
      const response = await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdGet({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        onboardingId: onboardingId,
      });
      setOnboarding(response.data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetch = useCallback(async () => {
    if (onboardingIdComputed) {
      fetchById(onboardingIdComputed);
    }
  }, [onboardingIdComputed]);

  const toggleIsShowOnLaunch = useCallback(async () => {
    if (!onboarding) {
      return;
    }

    // optimistic update
    const newValue = !onboarding.isShowOnLaunch;
    setOnboarding({ ...onboarding, isShowOnLaunch: newValue });

    const response =
      await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdIsShowOnLaunchTogglePost({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        onboardingId: onboarding.id || "",
      });
    setOnboarding(response.data);
  }, [onboarding]);

  const start = useCallback(async () => {
    if (!onboarding) {
      return;
    }

    const response = await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdStartPost({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      onboardingId: onboarding.id || "",
    });
    setOnboarding(response.data);
  }, [onboarding]);

  const complete = useCallback(async () => {
    if (!onboarding) {
      return;
    }

    const response =
      await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdCompletePost({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        onboardingId: onboarding.id || "",
      });
    setOnboarding(response.data);
  }, [onboarding]);

  const skip = useCallback(async () => {
    if (!onboarding || !onboarding.definition?.isSkippable) {
      return;
    }

    const response = await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdSkipPost({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      onboardingId: onboarding.id || "",
    });
    setOnboarding(response.data);
  }, [onboarding]);

  const startStep = useCallback(
    async (stepDefinitionId: string) => {
      if (!onboarding) {
        return;
      }

      const response =
        await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdStepsStepDefinitionIdStartPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            onboardingId: onboarding.id || "",
            stepDefinitionId: stepDefinitionId,
          },
        );
      setOnboarding(response.data);
    },
    [onboarding],
  );

  const completeStep = useCallback(
    async (stepDefinitionId: string) => {
      if (!onboarding) {
        return;
      }

      const response =
        await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdStepsStepDefinitionIdCompletePost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            onboardingId: onboarding.id || "",
            stepDefinitionId: stepDefinitionId,
          },
        );
      setOnboarding(response.data);
    },
    [onboarding],
  );

  const skipStep = useCallback(
    async (stepDefinitionId: string) => {
      const stepPair = getStepPairByDefinitionId(stepDefinitionId);
      if (!onboarding || !stepPair || !stepPair.definition?.isSkippable) {
        return;
      }

      const response =
        await apiClient.onboardingsApi.apiV1GuidanceOnboardingsOnboardingIdStepsStepDefinitionIdSkipPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            onboardingId: onboarding.id || "",
            stepDefinitionId: stepDefinitionId,
          },
        );
      setOnboarding(response.data);
    },
    [onboarding],
  );

  // NB: isShowOnLaunch currently means to redirect to onboarding page on launch
  // if root route is opened (/ or /dashboard),
  // not force redirect from any route.
  const isForceRedirectedOnLaunchRef = useRef(false);
  const forceRedirectOnLaunchOnce = useCallback(() => {
    if (statusComputed && !isForceRedirectedOnLaunchRef.current) {
      isForceRedirectedOnLaunchRef.current = true;

      // always redirect to onboarding if it's not started - it means user hasn't seen it yet
      if (statusComputed === OnboardingStatus.NotStarted) {
        const onboardingPath = ROUTE_PATH.ONBOARDING_VIEW();
        console.log(`Force redirecting to onboarding on launch once: ${onboardingPath}.`);
        history.push(onboardingPath);
        return true;
      }
    }
    return false;
  }, [statusComputed, history]);

  const isRedirectedOnLaunchRef = useRef(false);
  const redirectOnLaunchOnce = useCallback(() => {
    if ((statusComputed || isShowOnLaunchComputed) && !isRedirectedOnLaunchRef.current) {
      isRedirectedOnLaunchRef.current = true;

      // always redirect to onboarding if it's not started - it means user hasn't seen it yet
      if (
        statusComputed === OnboardingStatus.NotStarted ||
        (isShowOnLaunchComputed &&
          includes([OnboardingStatus.NotStarted, OnboardingStatus.InProgress], statusComputed))
      ) {
        const onboardingPath = ROUTE_PATH.ONBOARDING_VIEW();
        console.log(`Redirecting to onboarding on launch once: ${onboardingPath}.`);
        history.push(onboardingPath);
        return true;
      }
    }
    return false;
  }, [statusComputed, isShowOnLaunchComputed, history]);

  const handleOnboardingViewed = useCallback(async () => {
    isForceRedirectedOnLaunchRef.current = true;
    isRedirectedOnLaunchRef.current = true;
  }, []);

  // fetch if user has onboarding
  useEffect(() => {
    if (onboardingIdComputed && !onboarding) {
      fetchById(onboardingIdComputed);
    }
  }, [onboardingIdComputed, onboarding, fetchById]);

  // open popper initially if it's not completed
  const isPopperOpenInitiallyHandlerRef = useRef(false);
  const isPopperOpenInitially =
    isEnabled &&
    !isIniting &&
    !!onboarding &&
    onboarding.status !== OnboardingStatus.Completed &&
    onboarding.status !== OnboardingStatus.Skipped;
  if (onboarding && !isPopperOpenInitiallyHandlerRef.current) {
    isPopperOpenInitiallyHandlerRef.current = true;
    if (isPopperOpenInitially) {
      setIsPopperOpen(true);
    }
  }

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(currentTenant?.id, EntityType.Onboarding, onboarding?.id || ""),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      if (data?.entity) {
        setOnboarding(data.entity);
      }
    },
  });

  const value = useMemo<OnboardingContextValue>(
    () =>
      ({
        isEnabled,
        isIniting,
        isLoading,
        onboarding,
        enabledStepDefinitions,
        currentStepPair,
        nextStepPair,
        progress,
        popper: {
          isOpen: isPopperOpen,
          mode: popperMode,
          open: openPopper,
          close: closePopper,
          toggle: togglePopper,
          setMode: setPopperMode,
        },
        fetch,
        fetchById,
        set: setOnboarding,
        toggleIsShowOnLaunch,
        start,
        complete,
        skip,
        getStepPairByDefinitionId,
        getStepPairByType,
        getNextStepPair,
        startStep,
        completeStep,
        skipStep,
        forceRedirectOnLaunchOnce,
        redirectOnLaunchOnce,
        handleOnboardingViewed,
      }) satisfies OnboardingContextValue,
    [
      isEnabled,
      isIniting,
      isLoading,
      onboarding,
      enabledStepDefinitions,
      currentStepPair,
      nextStepPair,
      progress,
      stepPairsByStepDefinitionIdMap,
      stepPairsByTypeMap,
      isPopperOpen,
      popperMode,
      forceRedirectOnLaunchOnce,
      redirectOnLaunchOnce,
      setPopperMode,
      fetch,
      fetchById,
      setOnboarding,
      toggleIsShowOnLaunch,
      start,
      complete,
      skip,
      getStepPairByDefinitionId,
      getStepPairByType,
      getNextStepPair,
      startStep,
      completeStep,
      skipStep,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
